import React, { useState, useEffect, useCallback } from "react";
import { Mic, Trash2, Check, Headphones, Clock, ArrowLeft, Lock, LogOut } from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";

function LoginGate({ onAuth }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/inbox/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Credenziali errate");
      }
      const data = await res.json();
      localStorage.setItem("sbp-admin-token", data.token);
      onAuth(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center px-4">
      <form onSubmit={handleLogin} className="w-full max-w-sm" data-testid="admin-login-form">
        <div className="bg-[#0c1220] border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 bg-[#EA4335] rounded-2xl flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
          </div>
          <h1 className="text-white font-black text-xl text-center mb-1">Area Riservata</h1>
          <p className="text-gray-500 text-xs text-center mb-6">Accesso solo per amministratori</p>

          {error && (
            <div className="bg-[#EA4335]/10 border border-[#EA4335]/30 text-[#EA4335] text-xs font-bold px-4 py-2.5 rounded-xl mb-4 text-center" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#4285F4]/50 transition-colors"
              autoComplete="username"
              data-testid="admin-username"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#4285F4]/50 transition-colors"
              autoComplete="current-password"
              data-testid="admin-password"
            />
            <button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full bg-[#EA4335] hover:bg-[#d93025] disabled:opacity-40 text-white font-bold py-3 rounded-xl transition-all text-sm"
              data-testid="admin-login-btn"
            >
              {loading ? "Accesso..." : "Accedi"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function VoiceInboxPage() {
  const [token, setToken] = useState(() => localStorage.getItem("sbp-admin-token"));
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchNotes = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/admin/voice-notes`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { localStorage.removeItem("sbp-admin-token"); setToken(null); return; }
      const data = await res.json();
      setNotes(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const markListened = async (id) => {
    await fetch(`${API}/api/admin/voice-notes/${id}/listened`, { method: "POST", headers });
    setNotes(prev => prev.map(n => n.id === id ? { ...n, listened: true } : n));
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Eliminare questa nota vocale?")) return;
    await fetch(`${API}/api/admin/voice-notes/${id}`, { method: "DELETE", headers });
    setNotes(prev => prev.filter(n => n.id !== id));
  };

  const handlePlay = (id) => { markListened(id); };

  const logout = () => { localStorage.removeItem("sbp-admin-token"); setToken(null); };

  if (!token) return <LoginGate onAuth={setToken} />;

  const unlistened = notes.filter(n => !n.listened).length;
  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors" data-testid="inbox-back">
            <ArrowLeft className="w-4 h-4" /> Torna al sito
          </Link>
          <button onClick={logout} className="inline-flex items-center gap-1.5 text-gray-500 hover:text-[#EA4335] text-xs transition-colors" data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Esci
          </button>
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-black flex items-center gap-3">
              <div className="w-10 h-10 bg-[#EA4335] rounded-xl flex items-center justify-center">
                <Mic className="w-5 h-5 text-white" />
              </div>
              Inbox Vocale
            </h1>
            <p className="text-gray-500 text-sm mt-1">Note vocali ricevute dai clienti</p>
          </div>
          {unlistened > 0 && (
            <div className="bg-[#EA4335] text-white text-sm font-bold px-3 py-1.5 rounded-full" data-testid="unlistened-count">
              {unlistened} nuov{unlistened === 1 ? "a" : "e"}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#EA4335] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-inbox">
            <Headphones className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Nessuna nota vocale ricevuta</p>
            <p className="text-gray-600 text-sm mt-1">Quando un cliente registra una nota, apparira qui.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="voice-notes-list">
            {notes.map((note) => (
              <div
                key={note.id}
                className={`rounded-xl border transition-all ${note.listened ? "bg-white/5 border-white/10" : "bg-[#EA4335]/5 border-[#EA4335]/30"}`}
                data-testid={`voice-note-${note.id}`}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {!note.listened && <div className="w-2.5 h-2.5 rounded-full bg-[#EA4335] animate-pulse"></div>}
                      <div className="flex items-center gap-2 text-gray-400 text-xs">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(note.created_at)}
                      </div>
                      <span className="text-gray-600 text-xs">{note.size_kb} KB</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!note.listened && (
                        <button onClick={() => markListened(note.id)} className="text-gray-500 hover:text-[#34A853] transition-colors p-1.5 rounded-lg hover:bg-white/5" title="Segna come ascoltata">
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => deleteNote(note.id)} className="text-gray-500 hover:text-[#EA4335] transition-colors p-1.5 rounded-lg hover:bg-white/5" title="Elimina" data-testid={`delete-note-${note.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <audio
                    src={`${API}${note.url}`}
                    controls
                    className="w-full h-10"
                    style={{ filter: "invert(1)" }}
                    onPlay={() => handlePlay(note.id)}
                    data-testid={`audio-player-${note.id}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
